import { useQuery } from '@tanstack/react-query';
import { useApi } from './client.js';

export interface TravelReportRow {
  user_id: string;
  display_name: string;
  email: string;
  job_title: string | null;
  team_names: string[];
  journey_count: number;
  total_distance_m: number;
  avg_distance_per_journey_m: number;
}

export interface TravelReportQuery {
  from: string; // ISO date-time
  to: string;
  // Comma-joined team UUIDs. Omit / empty string to let the server choose:
  // monitors / admins → org-wide, leaders → their teams.
  teamIds?: string;
}

export function useTravelReport(q: TravelReportQuery, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'travel', q.from, q.to, q.teamIds ?? ''],
    queryFn: () => {
      const sp = new URLSearchParams({ from: q.from, to: q.to });
      if (q.teamIds) sp.set('team_ids', q.teamIds);
      return api<{ rows: TravelReportRow[] }>(`/reports/travel?${sp.toString()}`);
    },
    enabled,
    staleTime: 60_000,
  });
}

export interface CheckInReportRow {
  user_id: string;
  display_name: string;
  email: string;
  job_title: string | null;
  started_count: number;
  completed_count: number;
  overdue_count: number;
  cancelled_count: number;
  avg_duration_minutes: number;
  compliance_percent: number; // -1 = no completed/overdue yet
}

export function useCheckInReport(q: TravelReportQuery, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'check-ins', q.from, q.to, q.teamIds ?? ''],
    queryFn: () => {
      const sp = new URLSearchParams({ from: q.from, to: q.to });
      if (q.teamIds) sp.set('team_ids', q.teamIds);
      return api<{ rows: CheckInReportRow[] }>(`/reports/check-ins?${sp.toString()}`);
    },
    enabled,
    staleTime: 60_000,
  });
}

export interface AlarmReportRow {
  user_id: string;
  display_name: string;
  email: string;
  job_title: string | null;
  total_count: number;
  by_type: {
    duress: number;
    overdue_check_in: number;
    man_down: number;
    welfare_failed: number;
  };
  by_outcome: {
    real_incident: number;
    welfare_check_ok: number;
    false_alarm: number;
    unclassified: number;
  };
  currently_active: number;
  avg_ack_seconds: number | null;
  avg_resolve_seconds: number | null;
}

export function useAlarmReport(q: TravelReportQuery, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'alarms', q.from, q.to, q.teamIds ?? ''],
    queryFn: () => {
      const sp = new URLSearchParams({ from: q.from, to: q.to });
      if (q.teamIds) sp.set('team_ids', q.teamIds);
      return api<{ rows: AlarmReportRow[] }>(`/reports/alarms?${sp.toString()}`);
    },
    enabled,
    staleTime: 60_000,
  });
}

export type UtilisationStatus =
  | 'tracking_gap'
  | 'healthy'
  | 'update_needed'
  | 'no_background'
  | 'no_push'
  | 'stale';

export interface UtilisationReportRow {
  user_id: string;
  display_name: string;
  email: string;
  job_title: string | null;
  last_seen_at: string | null;
  has_device_api_token: boolean;
  device_api_token_label: string | null;
  latest_foreground_at: string | null;
  latest_background_at: string | null;
  has_push_token: boolean;
  on_shift: boolean;
  status: UtilisationStatus;
}

export interface UtilisationQuery {
  windowDays?: number;
  teamIds?: string;
}

export function useUtilisationReport(q: UtilisationQuery, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'utilisation', q.windowDays ?? '', q.teamIds ?? ''],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (q.windowDays !== undefined) sp.set('window_days', String(q.windowDays));
      if (q.teamIds) sp.set('team_ids', q.teamIds);
      const qs = sp.toString();
      return api<{ rows: UtilisationReportRow[] }>(`/reports/utilisation${qs ? `?${qs}` : ''}`);
    },
    enabled,
    staleTime: 60_000,
  });
}

// Reimbursement report. Per worker per day with each journey classified into
// inter-visit / office-bonus / commute / unclassified buckets. Used by payroll
// to figure out what should be paid for travel.
export interface ReimbursementRow {
  user_id: string;
  display_name: string;
  email: string;
  job_title: string | null;
  date: string; // YYYY-MM-DD
  visit_count: number;
  journey_count: number;
  total_distance_m: number;
  inter_visit_m: number;
  office_bonus_m: number;
  commute_m: number;
  unclassified_m: number;
  reimbursable_m: number;
}

export function useReimbursementReport(q: TravelReportQuery, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'reimbursement', q.from, q.to, q.teamIds ?? ''],
    queryFn: () => {
      const sp = new URLSearchParams({ from: q.from, to: q.to });
      if (q.teamIds) sp.set('team_ids', q.teamIds);
      return api<{ rows: ReimbursementRow[] }>(`/reports/reimbursement?${sp.toString()}`);
    },
    enabled,
    staleTime: 60_000,
  });
}

export interface OffShiftVisitRow {
  occurred_at: string;
  user_id: string | null;
  display_name: string | null;
  email: string | null;
  visit_id: string | null;
  client_display_name: string | null;
  scheduled_start: string | null;
  actual_arrival: string | null;
}

// Org-wide list (no team filter); from/to ISO date-times.
export function useOffShiftVisitsReport(q: { from: string; to: string }, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'off-shift-visits', q.from, q.to],
    queryFn: () => {
      const sp = new URLSearchParams({ from: q.from, to: q.to });
      return api<{ rows: OffShiftVisitRow[] }>(`/reports/off-shift-visits?${sp.toString()}`);
    },
    enabled,
    staleTime: 60_000,
  });
}
